import { useState, useMemo, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { HelpButton } from "@/components/help-button";
import { ContextualHelpDrawer } from "@/components/contextual-help-drawer";
import { useContextualHelp } from "@/hooks/use-contextual-help";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { 
  BarChart3, 
  RefreshCw, 
  Download, 
  Lock, 
  Unlock, 
  AlertTriangle,
  TrendingUp,
  Package,
  DollarSign,
  Calendar,
  Info,
  Settings,
  CheckCircle2,
  XCircle,
  ArrowUpDown,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  HelpCircle,
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { ParetoChart } from "@/components/abc-classification/pareto-chart";
import { toast } from "sonner";

// Types
interface ABCItem {
  code: string;
  description: string;
  annualUsageValue: number;
  percentOfTotal: number;
  cumulativePercent: number;
  currentClassification: "A" | "B" | "C";
  proposedClassification: "A" | "B" | "C";
  isOverridden: boolean;
  overrideReason?: string;
  overrideBy?: string;
  overrideDate?: Date;
  lastCountDate?: Date;
  nextCountDate?: Date;
  monthsOfHistory: number;
  category?: string;
  unitCost: number;
  annualQuantitySold: number;
}

interface ABCConfiguration {
  method: "annual-usage" | "current-stock" | "manual";
  aThreshold: number;
  bThreshold: number;
  analysisPeriod: 3 | 6 | 12;
  autoRecalculation: "weekly" | "monthly" | "quarterly" | "disabled";
  newItemDefault: "A" | "B" | "C" | "inherit";
  zeroMovementHandling: "exclude" | "assign-c" | "retain";
}

interface ABCSummary {
  aCount: number;
  bCount: number;
  cCount: number;
  aValue: number;
  bValue: number;
  cValue: number;
  totalItems: number;
  totalValue: number;
  lastCalculation?: Date;
  nextCalculation?: Date;
}

interface OverrideDialogData {
  item: ABCItem;
  newClassification: "A" | "B" | "C";
}

// Mock data generator
function generateMockABCData(config: ABCConfiguration): ABCItem[] {
  const items: ABCItem[] = [];
  const itemCount = 500;

  // Generate items with varying annual usage values
  for (let i = 0; i < itemCount; i++) {
    const code = `ITEM-${String(i + 1).padStart(4, "0")}`;
    const annualQuantitySold = Math.floor(Math.random() * 10000) + 100;
    const unitCost = parseFloat((Math.random() * 500 + 10).toFixed(2));
    const annualUsageValue = annualQuantitySold * unitCost;
    const monthsOfHistory = Math.floor(Math.random() * 12) + 1;
    
    items.push({
      code,
      description: `Stock Item ${i + 1}`,
      annualUsageValue,
      percentOfTotal: 0, // Will be calculated
      cumulativePercent: 0, // Will be calculated
      currentClassification: Math.random() < 0.2 ? "A" : Math.random() < 0.5 ? "B" : "C",
      proposedClassification: "C", // Will be calculated
      isOverridden: Math.random() < 0.1,
      monthsOfHistory,
      category: ["Electronics", "Furniture", "Office Supplies", "Hardware", "Tools"][Math.floor(Math.random() * 5)],
      unitCost,
      annualQuantitySold,
    });
  }

  // Sort by annual usage value descending
  items.sort((a, b) => b.annualUsageValue - a.annualUsageValue);

  // Calculate percentages and cumulative
  const totalValue = items.reduce((sum, item) => sum + item.annualUsageValue, 0);
  let cumulative = 0;

  items.forEach((item) => {
    item.percentOfTotal = (item.annualUsageValue / totalValue) * 100;
    cumulative += item.percentOfTotal;
    item.cumulativePercent = cumulative;

    // Assign proposed classification based on thresholds
    if (item.cumulativePercent <= config.aThreshold) {
      item.proposedClassification = "A";
    } else if (item.cumulativePercent <= config.aThreshold + config.bThreshold) {
      item.proposedClassification = "B";
    } else {
      item.proposedClassification = "C";
    }

    // If overridden, keep current classification
    if (item.isOverridden) {
      item.proposedClassification = item.currentClassification;
      item.overrideReason = ["Critical/Strategic Item", "Customer Requirement", "Supplier Constraint"][Math.floor(Math.random() * 3)];
      item.overrideBy = "Admin User";
      item.overrideDate = new Date(Date.now() - Math.random() * 90 * 24 * 60 * 60 * 1000);
    }

    // Generate count dates
    const daysAgo = Math.floor(Math.random() * 90);
    item.lastCountDate = new Date(Date.now() - daysAgo * 24 * 60 * 60 * 1000);
    
    // Calculate next count date based on classification
    const countFrequency = item.currentClassification === "A" ? 30 : item.currentClassification === "B" ? 90 : 365;
    item.nextCountDate = new Date(item.lastCountDate.getTime() + countFrequency * 24 * 60 * 60 * 1000);
  });

  return items;
}

export default function ABCClassificationPage() {
  const { isHelpOpen, helpContent, toggleHelp, setIsHelpOpen } = useContextualHelp("abc-classification");
  const [isLoading, setIsLoading] = useState(true);
  const [isCalculating, setIsCalculating] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  
  // Configuration state
  const [config, setConfig] = useState<ABCConfiguration>({
    method: "annual-usage",
    aThreshold: 80,
    bThreshold: 15,
    analysisPeriod: 12,
    autoRecalculation: "monthly",
    newItemDefault: "C",
    zeroMovementHandling: "assign-c",
  });

  // Data state
  const [items, setItems] = useState<ABCItem[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [classificationFilter, setClassificationFilter] = useState<"all" | "A" | "B" | "C">("all");
  const [changesOnlyFilter, setChangesOnlyFilter] = useState(false);
  const [overrideFilter, setOverrideFilter] = useState(false);
  const [sortColumn, setSortColumn] = useState<keyof ABCItem>("cumulativePercent");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;

  // Dialog states
  const [overrideDialog, setOverrideDialog] = useState<OverrideDialogData | null>(null);
  const [overrideReason, setOverrideReason] = useState("");
  const [overrideReasonText, setOverrideReasonText] = useState("");
  const [applyChangesDialog, setApplyChangesDialog] = useState(false);
  const [helpDialog, setHelpDialog] = useState(false);

  // Selection state
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());

  // Initialize data
  useEffect(() => {
    const timer = setTimeout(() => {
      setItems(generateMockABCData(config));
      setIsLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  // Calculate summary statistics
  const summary: ABCSummary = useMemo(() => {
    const aItems = items.filter(i => i.proposedClassification === "A");
    const bItems = items.filter(i => i.proposedClassification === "B");
    const cItems = items.filter(i => i.proposedClassification === "C");

    return {
      aCount: aItems.length,
      bCount: bItems.length,
      cCount: cItems.length,
      aValue: aItems.reduce((sum, i) => sum + i.annualUsageValue, 0),
      bValue: bItems.reduce((sum, i) => sum + i.annualUsageValue, 0),
      cValue: cItems.reduce((sum, i) => sum + i.annualUsageValue, 0),
      totalItems: items.length,
      totalValue: items.reduce((sum, i) => sum + i.annualUsageValue, 0),
      lastCalculation: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
      nextCalculation: new Date(Date.now() + 28 * 24 * 60 * 60 * 1000),
    };
  }, [items]);

  // Filter and sort items
  const filteredItems = useMemo(() => {
    let filtered = items;

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        item => 
          item.code.toLowerCase().includes(query) || 
          item.description.toLowerCase().includes(query)
      );
    }

    // Classification filter
    if (classificationFilter !== "all") {
      filtered = filtered.filter(item => item.proposedClassification === classificationFilter);
    }

    // Changes only filter
    if (changesOnlyFilter) {
      filtered = filtered.filter(item => item.currentClassification !== item.proposedClassification);
    }

    // Override filter
    if (overrideFilter) {
      filtered = filtered.filter(item => item.isOverridden);
    }

    // Sort
    filtered.sort((a, b) => {
      const aVal = a[sortColumn];
      const bVal = b[sortColumn];
      const modifier = sortDirection === "asc" ? 1 : -1;
      
      if (typeof aVal === "number" && typeof bVal === "number") {
        return (aVal - bVal) * modifier;
      }
      return String(aVal).localeCompare(String(bVal)) * modifier;
    });

    return filtered;
  }, [items, searchQuery, classificationFilter, changesOnlyFilter, overrideFilter, sortColumn, sortDirection]);

  // Pagination
  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
  const paginatedItems = filteredItems.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Count items with changes
  const changesCount = items.filter(i => i.currentClassification !== i.proposedClassification).length;

  // Count overdue items
  const overdueCount = items.filter(i => i.nextCountDate && i.nextCountDate < new Date()).length;

  // Handle recalculation
  const handleRecalculate = () => {
    setIsCalculating(true);
    setTimeout(() => {
      const newItems = generateMockABCData(config);
      setItems(newItems);
      setIsCalculating(false);
      toast.success("Classification Recalculated", {
        description: `${newItems.length} items have been reclassified based on current settings.`,
      });
    }, 2000);
  };

  // Handle configuration change
  const handleConfigChange = (key: keyof ABCConfiguration, value: any) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  // Handle apply changes
  const handleApplyChanges = () => {
    setApplyChangesDialog(false);
    setIsCalculating(true);
    
    setTimeout(() => {
      // Update current classifications to match proposed
      const updatedItems = items.map(item => ({
        ...item,
        currentClassification: item.proposedClassification,
      }));
      setItems(updatedItems);
      setIsCalculating(false);
      
      toast.success("Classifications Applied", {
        description: `${changesCount} items have been updated successfully.`,
      });
    }, 1500);
  };

  // Handle override
  const handleOverride = (item: ABCItem, newClassification: "A" | "B" | "C") => {
    setOverrideDialog({ item, newClassification });
    setOverrideReason("");
    setOverrideReasonText("");
  };

  const handleOverrideConfirm = () => {
    if (!overrideDialog || !overrideReason) return;

    const updatedItems = items.map(item => {
      if (item.code === overrideDialog.item.code) {
        return {
          ...item,
          currentClassification: overrideDialog.newClassification,
          proposedClassification: overrideDialog.newClassification,
          isOverridden: true,
          overrideReason: overrideReason === "other" ? overrideReasonText : overrideReason,
          overrideBy: "Current User",
          overrideDate: new Date(),
        };
      }
      return item;
    });

    setItems(updatedItems);
    setOverrideDialog(null);
    
    toast.success("Override Applied", {
      description: `${overrideDialog.item.code} has been manually classified as ${overrideDialog.newClassification}.`,
    });
  };

  // Handle unlock override
  const handleUnlockOverride = (itemCode: string) => {
    const updatedItems = items.map(item => {
      if (item.code === itemCode) {
        return {
          ...item,
          isOverridden: false,
          overrideReason: undefined,
          overrideBy: undefined,
          overrideDate: undefined,
        };
      }
      return item;
    });

    setItems(updatedItems);
    
    toast.success("Override Removed", {
      description: `${itemCode} will now be automatically classified.`,
    });
  };

  // Handle export
  const handleExport = () => {
    const csv = [
      ["SKU", "Description", "Annual Usage Value", "% of Total", "Cumulative %", "Current Class", "Proposed Class", "Override Status", "Override Reason"].join(","),
      ...filteredItems.map(item => [
        item.code,
        `"${item.description}"`,
        item.annualUsageValue.toFixed(2),
        item.percentOfTotal.toFixed(2),
        item.cumulativePercent.toFixed(2),
        item.currentClassification,
        item.proposedClassification,
        item.isOverridden ? "Yes" : "No",
        item.overrideReason ? `"${item.overrideReason}"` : "",
      ].join(","))
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `abc-classification-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);

    toast.success("Export Complete", {
      description: "ABC classification data has been exported to CSV.",
    });
  };

  // Handle sort
  const handleSort = (column: keyof ABCItem) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-64" />
        <div className="grid gap-4 md:grid-cols-4">
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">ABC Classification</h1>
          <p className="text-muted-foreground mt-1">
            Automatically categorise inventory by value and optimise counting cycles
          </p>
        </div>
        <div className="flex items-center gap-2">
          <HelpButton onClick={toggleHelp} />
          <Button variant="outline" onClick={handleExport}>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          <Button onClick={handleRecalculate} disabled={isCalculating}>
            <RefreshCw className={cn("h-4 w-4 mr-2", isCalculating && "animate-spin")} />
            {isCalculating ? "Calculating..." : "Recalculate Now"}
          </Button>
        </div>
      </div>

      {/* Alerts */}
      {changesCount > 0 && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription className="flex items-center justify-between">
            <span>
              {changesCount} item{changesCount !== 1 ? "s" : ""} will change classification. Review and apply changes.
            </span>
            <Button size="sm" onClick={() => setApplyChangesDialog(true)}>
              Review Changes
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {overdueCount > 0 && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            {overdueCount} item{overdueCount !== 1 ? "s are" : " is"} overdue for cycle counting.
          </AlertDescription>
        </Alert>
      )}

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">A-Class Items</CardTitle>
            <Package className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary.aCount}</div>
            <p className="text-xs text-muted-foreground">
              {((summary.aCount / summary.totalItems) * 100).toFixed(1)}% of items
            </p>
            <div className="mt-2 flex items-center text-xs">
              <DollarSign className="h-3 w-3 mr-1" />
              <span className="font-medium">
                {((summary.aValue / summary.totalValue) * 100).toFixed(1)}% of value
              </span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">B-Class Items</CardTitle>
            <Package className="h-4 w-4 text-yellow-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary.bCount}</div>
            <p className="text-xs text-muted-foreground">
              {((summary.bCount / summary.totalItems) * 100).toFixed(1)}% of items
            </p>
            <div className="mt-2 flex items-center text-xs">
              <DollarSign className="h-3 w-3 mr-1" />
              <span className="font-medium">
                {((summary.bValue / summary.totalValue) * 100).toFixed(1)}% of value
              </span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">C-Class Items</CardTitle>
            <Package className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary.cCount}</div>
            <p className="text-xs text-muted-foreground">
              {((summary.cCount / summary.totalItems) * 100).toFixed(1)}% of items
            </p>
            <div className="mt-2 flex items-center text-xs">
              <DollarSign className="h-3 w-3 mr-1" />
              <span className="font-medium">
                {((summary.cValue / summary.totalValue) * 100).toFixed(1)}% of value
              </span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Next Recalculation</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {summary.nextCalculation?.toLocaleDateString("en-US", { month: "short", day: "numeric" })}
            </div>
            <p className="text-xs text-muted-foreground">
              Last: {summary.lastCalculation?.toLocaleDateString()}
            </p>
            <div className="mt-2 text-xs text-muted-foreground">
              Auto: {config.autoRecalculation}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">
            <BarChart3 className="h-4 w-4 mr-2" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="items">
            <Package className="h-4 w-4 mr-2" />
            Items ({filteredItems.length})
          </TabsTrigger>
          <TabsTrigger value="configuration">
            <Settings className="h-4 w-4 mr-2" />
            Configuration
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <ParetoChart 
            items={items} 
            aThreshold={config.aThreshold}
            bThreshold={config.bThreshold}
          />

          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Classification Distribution</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">A-Class (High Value)</span>
                    <span className="text-sm text-muted-foreground">{summary.aCount} items</span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-red-600" 
                      style={{ width: `${(summary.aCount / summary.totalItems) * 100}%` }}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">B-Class (Medium Value)</span>
                    <span className="text-sm text-muted-foreground">{summary.bCount} items</span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-yellow-600" 
                      style={{ width: `${(summary.bCount / summary.totalItems) * 100}%` }}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">C-Class (Low Value)</span>
                    <span className="text-sm text-muted-foreground">{summary.cCount} items</span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-green-600" 
                      style={{ width: `${(summary.cCount / summary.totalItems) * 100}%` }}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Value Distribution</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">A-Class Value</span>
                    <span className="text-sm text-muted-foreground">
                      ${summary.aValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-red-600" 
                      style={{ width: `${(summary.aValue / summary.totalValue) * 100}%` }}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">B-Class Value</span>
                    <span className="text-sm text-muted-foreground">
                      ${summary.bValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-yellow-600" 
                      style={{ width: `${(summary.bValue / summary.totalValue) * 100}%` }}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">C-Class Value</span>
                    <span className="text-sm text-muted-foreground">
                      ${summary.cValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-green-600" 
                      style={{ width: `${(summary.cValue / summary.totalValue) * 100}%` }}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Items Tab */}
        <TabsContent value="items" className="space-y-4">
          {/* Filters */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-wrap gap-4">
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search by SKU or description..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-8"
                    />
                  </div>
                </div>

                <Select value={classificationFilter} onValueChange={(v: any) => setClassificationFilter(v)}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Classes</SelectItem>
                    <SelectItem value="A">A-Class</SelectItem>
                    <SelectItem value="B">B-Class</SelectItem>
                    <SelectItem value="C">C-Class</SelectItem>
                  </SelectContent>
                </Select>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="changes-only"
                    checked={changesOnlyFilter}
                    onCheckedChange={(checked) => setChangesOnlyFilter(checked as boolean)}
                  />
                  <Label htmlFor="changes-only" className="cursor-pointer">
                    Changes only
                  </Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="overrides-only"
                    checked={overrideFilter}
                    onCheckedChange={(checked) => setOverrideFilter(checked as boolean)}
                  />
                  <Label htmlFor="overrides-only" className="cursor-pointer">
                    Overrides only
                  </Label>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Items Table */}
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[50px]">
                        <Checkbox
                          checked={selectedItems.size === paginatedItems.length && paginatedItems.length > 0}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedItems(new Set(paginatedItems.map(i => i.code)));
                            } else {
                              setSelectedItems(new Set());
                            }
                          }}
                        />
                      </TableHead>
                      <TableHead>
                        <Button variant="ghost" size="sm" onClick={() => handleSort("code")}>
                          SKU
                          <ArrowUpDown className="ml-2 h-3 w-3" />
                        </Button>
                      </TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => handleSort("annualUsageValue")}>
                          Annual Value
                          <ArrowUpDown className="ml-2 h-3 w-3" />
                        </Button>
                      </TableHead>
                      <TableHead className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => handleSort("percentOfTotal")}>
                          % of Total
                          <ArrowUpDown className="ml-2 h-3 w-3" />
                        </Button>
                      </TableHead>
                      <TableHead className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => handleSort("cumulativePercent")}>
                          Cumulative %
                          <ArrowUpDown className="ml-2 h-3 w-3" />
                        </Button>
                      </TableHead>
                      <TableHead>Current</TableHead>
                      <TableHead>Proposed</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedItems.map((item) => (
                      <TableRow 
                        key={item.code}
                        className={cn(
                          item.currentClassification !== item.proposedClassification && "bg-yellow-50"
                        )}
                      >
                        <TableCell>
                          <Checkbox
                            checked={selectedItems.has(item.code)}
                            onCheckedChange={(checked) => {
                              const newSelected = new Set(selectedItems);
                              if (checked) {
                                newSelected.add(item.code);
                              } else {
                                newSelected.delete(item.code);
                              }
                              setSelectedItems(newSelected);
                            }}
                          />
                        </TableCell>
                        <TableCell className="font-medium">{item.code}</TableCell>
                        <TableCell>
                          <div className="max-w-[300px] truncate">{item.description}</div>
                          {item.monthsOfHistory < 12 && (
                            <Badge variant="outline" className="mt-1 text-xs">
                              {item.monthsOfHistory}mo data
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          ${item.annualUsageValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                        </TableCell>
                        <TableCell className="text-right">
                          {item.percentOfTotal.toFixed(2)}%
                        </TableCell>
                        <TableCell className="text-right">
                          {item.cumulativePercent.toFixed(2)}%
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              item.currentClassification === "A" 
                                ? "destructive" 
                                : item.currentClassification === "B" 
                                ? "default" 
                                : "secondary"
                            }
                          >
                            {item.currentClassification}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              item.proposedClassification === "A" 
                                ? "destructive" 
                                : item.proposedClassification === "B" 
                                ? "default" 
                                : "secondary"
                            }
                          >
                            {item.proposedClassification}
                          </Badge>
                          {item.currentClassification !== item.proposedClassification && (
                            <Badge variant="outline" className="ml-2 text-xs">
                              Changed
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {item.isOverridden ? (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger>
                                  <Lock className="h-4 w-4 text-orange-600" />
                                </TooltipTrigger>
                                <TooltipContent>
                                  <div className="text-xs">
                                    <div className="font-medium">Manual Override</div>
                                    <div>{item.overrideReason}</div>
                                    <div className="text-muted-foreground">
                                      By {item.overrideBy} on {item.overrideDate?.toLocaleDateString()}
                                    </div>
                                  </div>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ) : (
                            <CheckCircle2 className="h-4 w-4 text-green-600" />
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            {item.isOverridden ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleUnlockOverride(item.code)}
                              >
                                <Unlock className="h-3 w-3" />
                              </Button>
                            ) : (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleOverride(item, "A")}
                                  disabled={item.proposedClassification === "A"}
                                >
                                  A
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleOverride(item, "B")}
                                  disabled={item.proposedClassification === "B"}
                                >
                                  B
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleOverride(item, "C")}
                                  disabled={item.proposedClassification === "C"}
                                >
                                  C
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between px-4 py-4 border-t">
                <div className="text-sm text-muted-foreground">
                  Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredItems.length)} of {filteredItems.length} items
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>
                  <div className="text-sm">
                    Page {currentPage} of {totalPages}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Configuration Tab */}
        <TabsContent value="configuration" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Classification Method</CardTitle>
              <CardDescription>
                Choose how items should be classified
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Calculation Method</Label>
                <Select 
                  value={config.method} 
                  onValueChange={(v: any) => handleConfigChange("method", v)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="annual-usage">Annual Usage Value (Qty × Cost)</SelectItem>
                    <SelectItem value="current-stock">Current Stock Value</SelectItem>
                    <SelectItem value="manual">Manual Only</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {config.method === "annual-usage" && "Recommended: Based on sales velocity and cost"}
                  {config.method === "current-stock" && "Based on current inventory value"}
                  {config.method === "manual" && "All classifications must be set manually"}
                </p>
              </div>

              <div className="space-y-2">
                <Label>Analysis Period</Label>
                <Select 
                  value={config.analysisPeriod.toString()} 
                  onValueChange={(v) => handleConfigChange("analysisPeriod", parseInt(v))}
                  disabled={config.method === "manual"}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="3">3 Months</SelectItem>
                    <SelectItem value="6">6 Months</SelectItem>
                    <SelectItem value="12">12 Months (Recommended)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Classification Thresholds</CardTitle>
              <CardDescription>
                Define the cumulative value percentages for each class
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>A-Class Threshold</Label>
                  <span className="text-sm font-medium">{config.aThreshold}%</span>
                </div>
                <Input
                  type="range"
                  min="50"
                  max="90"
                  step="5"
                  value={config.aThreshold}
                  onChange={(e) => handleConfigChange("aThreshold", parseInt(e.target.value))}
                  disabled={config.method === "manual"}
                />
                <p className="text-xs text-muted-foreground">
                  Items representing the first {config.aThreshold}% of total value
                </p>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>B-Class Threshold</Label>
                  <span className="text-sm font-medium">{config.bThreshold}%</span>
                </div>
                <Input
                  type="range"
                  min="5"
                  max="30"
                  step="5"
                  value={config.bThreshold}
                  onChange={(e) => handleConfigChange("bThreshold", parseInt(e.target.value))}
                  disabled={config.method === "manual"}
                />
                <p className="text-xs text-muted-foreground">
                  Items representing {config.aThreshold}% to {config.aThreshold + config.bThreshold}% of total value
                </p>
              </div>

              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                  C-Class will automatically include remaining items ({100 - config.aThreshold - config.bThreshold}% of value)
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Automatic Recalculation</CardTitle>
              <CardDescription>
                Schedule when classifications should be automatically updated
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Recalculation Frequency</Label>
                <Select 
                  value={config.autoRecalculation} 
                  onValueChange={(v: any) => handleConfigChange("autoRecalculation", v)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly (Recommended)</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="disabled">Disabled</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {config.autoRecalculation !== "disabled" && (
                <Alert>
                  <Calendar className="h-4 w-4" />
                  <AlertDescription>
                    Next automatic recalculation: {summary.nextCalculation?.toLocaleString()}
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Special Cases</CardTitle>
              <CardDescription>
                Define how to handle items with insufficient or no data
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>New Items (Less than 3 months history)</Label>
                <Select 
                  value={config.newItemDefault} 
                  onValueChange={(v: any) => handleConfigChange("newItemDefault", v)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="A">Assign to A-Class</SelectItem>
                    <SelectItem value="B">Assign to B-Class</SelectItem>
                    <SelectItem value="C">Assign to C-Class (Recommended)</SelectItem>
                    <SelectItem value="inherit">Inherit from Category</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Zero-Movement Items</Label>
                <Select 
                  value={config.zeroMovementHandling} 
                  onValueChange={(v: any) => handleConfigChange("zeroMovementHandling", v)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="exclude">Exclude from Classification</SelectItem>
                    <SelectItem value="assign-c">Assign to C-Class (Recommended)</SelectItem>
                    <SelectItem value="retain">Retain Previous Classification</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Override Dialog */}
      <Dialog open={!!overrideDialog} onOpenChange={() => setOverrideDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Manual Classification Override</DialogTitle>
            <DialogDescription>
              Override automatic classification for {overrideDialog?.item.code}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
              <div>
                <div className="font-medium">{overrideDialog?.item.code}</div>
                <div className="text-sm text-muted-foreground">{overrideDialog?.item.description}</div>
              </div>
              <div className="text-right">
                <div className="text-sm text-muted-foreground">Proposed</div>
                <Badge variant="destructive">{overrideDialog?.newClassification}</Badge>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Override Reason *</Label>
              <Select value={overrideReason} onValueChange={setOverrideReason}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a reason..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Critical/Strategic Item">Critical/Strategic Item</SelectItem>
                  <SelectItem value="Customer Requirement">Customer Requirement</SelectItem>
                  <SelectItem value="Supplier Constraint">Supplier Constraint</SelectItem>
                  <SelectItem value="Seasonal Consideration">Seasonal Consideration</SelectItem>
                  <SelectItem value="other">Other (specify below)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {overrideReason === "other" && (
              <div className="space-y-2">
                <Label>Explanation *</Label>
                <Textarea
                  value={overrideReasonText}
                  onChange={(e) => setOverrideReasonText(e.target.value)}
                  placeholder="Explain why this override is necessary..."
                  rows={3}
                />
              </div>
            )}

            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                This item will be locked at {overrideDialog?.newClassification}-Class and excluded from automatic recalculations.
              </AlertDescription>
            </Alert>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOverrideDialog(null)}>
              Cancel
            </Button>
            <Button 
              onClick={handleOverrideConfirm}
              disabled={!overrideReason || (overrideReason === "other" && !overrideReasonText.trim())}
            >
              Apply Override
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Apply Changes Dialog */}
      <Dialog open={applyChangesDialog} onOpenChange={setApplyChangesDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Apply Classification Changes</DialogTitle>
            <DialogDescription>
              Review the changes that will be applied to your inventory
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              {["A", "B", "C"].map((from) =>
                ["A", "B", "C"].map((to) => {
                  if (from === to) return null;
                  const count = items.filter(
                    i => i.currentClassification === from && i.proposedClassification === to
                  ).length;
                  if (count === 0) return null;
                  
                  return (
                    <div key={`${from}-${to}`} className="p-3 border rounded-lg">
                      <div className="text-2xl font-bold">{count}</div>
                      <div className="text-xs text-muted-foreground">
                        {from} → {to}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <Separator />

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm">Total items affected:</span>
                <span className="font-bold">{changesCount}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm">Items with overrides:</span>
                <span className="font-bold">{items.filter(i => i.isOverridden).length}</span>
              </div>
            </div>

            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                This will update the ABC classification for {changesCount} items and recalculate their cycle count schedules.
              </AlertDescription>
            </Alert>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setApplyChangesDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleApplyChanges}>
              Apply Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Help Dialog */}
      <Dialog open={helpDialog} onOpenChange={setHelpDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>ABC Classification Help</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div>
              <h3 className="font-semibold mb-2">What is ABC Classification?</h3>
              <p className="text-sm text-muted-foreground">
                ABC Classification is an inventory categorization technique that divides items into three categories 
                based on their importance and value. It follows the Pareto Principle (80/20 rule):
              </p>
              <ul className="list-disc list-inside text-sm text-muted-foreground mt-2 space-y-1">
                <li><strong>A-Class:</strong> High-value items (typically 20% of items, 80% of value)</li>
                <li><strong>B-Class:</strong> Medium-value items (typically 30% of items, 15% of value)</li>
                <li><strong>C-Class:</strong> Low-value items (typically 50% of items, 5% of value)</li>
              </ul>
            </div>

            <Separator />

            <div>
              <h3 className="font-semibold mb-2">How are Classifications Calculated?</h3>
              <p className="text-sm text-muted-foreground">
                The system calculates Annual Usage Value for each item using the formula:
              </p>
              <div className="bg-muted p-3 rounded-lg my-2 font-mono text-sm">
                Annual Usage Value = Annual Quantity Sold × Average Unit Cost
              </div>
              <p className="text-sm text-muted-foreground">
                Items are then sorted by value and classified based on cumulative percentage thresholds.
              </p>
            </div>

            <Separator />

            <div>
              <h3 className="font-semibold mb-2">Why Override Classifications?</h3>
              <p className="text-sm text-muted-foreground">
                Manual overrides allow you to classify items based on factors beyond value:
              </p>
              <ul className="list-disc list-inside text-sm text-muted-foreground mt-2 space-y-1">
                <li>Strategic importance to the business</li>
                <li>Customer-specific requirements</li>
                <li>Supplier constraints or lead times</li>
                <li>Seasonal demand patterns</li>
              </ul>
            </div>

            <Separator />

            <div>
              <h3 className="font-semibold mb-2">Integration with Cycle Counting</h3>
              <p className="text-sm text-muted-foreground">
                ABC classifications automatically determine cycle count frequency:
              </p>
              <ul className="list-disc list-inside text-sm text-muted-foreground mt-2 space-y-1">
                <li><strong>A-Class:</strong> Counted more frequently (e.g., monthly)</li>
                <li><strong>B-Class:</strong> Counted moderately (e.g., quarterly)</li>
                <li><strong>C-Class:</strong> Counted less frequently (e.g., annually)</li>
              </ul>
              <p className="text-sm text-muted-foreground mt-2">
                Configure frequencies in Settings → Stock Options → Stock Take & Counting.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button onClick={() => setHelpDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Help Drawer */}
      <ContextualHelpDrawer
        open={isHelpOpen}
        onOpenChange={setIsHelpOpen}
        helpContent={helpContent}
      />
    </div>
  );
}
